import React, { createContext, useCallback, useMemo, useState } from 'react';
import { Data } from '../data/Data';
import { useNotificationsManager } from './notificationContext';
import { useResponsiveSize } from './responsiveContext';

const LotCommercialContext = createContext();
const LotCommercialManagerContext = createContext();

export const LotCommercialContextProvider = ({ children }) => {

    const { dismiss, extractErrorMessages, inform } = useNotificationsManager();
    const { navBarMaxItems } = useResponsiveSize();

    const [idLotCommercialActif, setIdLotCommercialActif] = useState(undefined);
    const [idOperationActive, setIdOperationActive] = useState(undefined);
    const [lotsCommerciaux, setLotsCommerciaux] = useState([]);

    const lotCommercialActif = useMemo(() => {
        return lotsCommerciaux.find(lot => lot.idOperation === idOperationActive && lot.idLotCommercial === idLotCommercialActif);
    }, [idLotCommercialActif, idOperationActive, lotsCommerciaux]);

    const resetLotsCommerciaux = useCallback(() => {
        setIdOperationActive(0);
        setIdLotCommercialActif(0);
        setLotsCommerciaux([]);
    }, []);

    const loadLotsCommerciaux = useCallback(() => {
        const id = inform("Chargement des lots commerciaux...");

        Data._getLotsCommerciaux().then(response => {
            if (response.codeRetour === 0) {
                const objet = response.reponseAPIobjet;
                setIdOperationActive(objet.lotsCommerciaux.length ? objet.lotsCommerciaux[0].idOperation : 0);
                setIdLotCommercialActif(objet.lotsCommerciaux.length ? objet.lotsCommerciaux[0].idLotCommercial : 0);
                setLotsCommerciaux(objet.lotsCommerciaux);
            }
            else {
                extractErrorMessages(response);
                resetLotsCommerciaux();
            }

            dismiss(id);
        });
    }, [dismiss, extractErrorMessages, inform, resetLotsCommerciaux]);

    const updateLotCommercialActif = useCallback((idOperation, idLotCommercial) => {

        setIdOperationActive(idOperation);
        setIdLotCommercialActif(idLotCommercial);

        // Si le lot commercial actif est dans la dropdown, le faire remonter dans la navbar
        const index = lotsCommerciaux.findIndex(lot => lot.idOperation === idOperation && lot.idLotCommercial === idLotCommercial);
        if (index > navBarMaxItems - 1)
            setLotsCommerciaux(prev => {
                let lotCommerciaux = [...prev];
                const lotCommercialActif = lotCommerciaux.splice(index, 1)[0];
                const replaced = lotCommerciaux.splice(navBarMaxItems - 1, 1, lotCommercialActif)[0];
                lotCommerciaux.push(replaced);
                return lotCommerciaux;
            });

    }, [lotsCommerciaux, navBarMaxItems]);

    const value = useMemo(
        () => ({ lotCommercialActif, lotsCommerciaux, idOperationActive, idLotCommercialActif }),
        [lotCommercialActif, lotsCommerciaux, idOperationActive, idLotCommercialActif]
    );

    const managerValue = useMemo(
        () => ({ loadLotsCommerciaux, resetLotsCommerciaux, updateLotCommercialActif }),
        [loadLotsCommerciaux, resetLotsCommerciaux, updateLotCommercialActif]
    );

    return <LotCommercialManagerContext.Provider value={managerValue}>
        <LotCommercialContext.Provider value={value}>
            {children}
        </LotCommercialContext.Provider>
    </LotCommercialManagerContext.Provider>;

};

export const useLotsCommerciaux = () => {
    const data = React.useContext(LotCommercialContext);
    if (data === undefined) throw new Error();
    const { lotCommercialActif, lotsCommerciaux, idOperationActive, idLotCommercialActif } = data;
    return { lotCommercialActif, lotsCommerciaux, idOperationActive, idLotCommercialActif };
};

export const useLotsCommerciauxManager = () => {
    const manager = React.useContext(LotCommercialManagerContext);
    if (manager === undefined) throw new Error();
    const { loadLotsCommerciaux, resetLotsCommerciaux, updateLotCommercialActif } = manager;
    return { loadLotsCommerciaux, resetLotsCommerciaux, updateLotCommercialActif };
};